/**
 * Redactions live here as rectangles, not as burnt-in pixels.
 *
 * The old design baked black boxes into the decoded pixel data and then had to
 * persist the result (plus a pre-redaction copy for undo) so a cache eviction
 * couldn't lose it. That made every redaction cost two full-frame writes and made
 * undo dependent on storage staying healthy.
 *
 * Now the rectangles are the source of truth. `applyTo` runs inside the image
 * loaders, so every frame is redacted as it's decoded — the viewer, the export
 * writer, and the ZIP path all get redacted pixels without any of them knowing
 * about redaction. Undo is deleting a map entry.
 *
 * Keyed per instance via `keyFor` (see below), because redaction is committed for
 * a whole series at a time and applies to every frame of every instance in it. The
 * key survives frame trimming — `applyEdits` rewrites `instance.frames` but never
 * `instance.imageId`.
 *
 * A missed lookup here is silent by design (most instances have no redaction), so
 * it can't be distinguished from "nothing to do". That makes the write-side
 * verification in export/verify-redaction.ts load-bearing: it re-checks the bytes
 * actually being written against these same rectangles.
 */

import { TypedArray } from "../types/ImageFrame";
// Imported directly rather than via cornerstone-setup.js to stay out of the
// loader<->setup import cycle (same reasoning as instance-source.ts).
import cornerstoneWADOImageLoader from "cornerstone-wado-image-loader";

const { parseImageId } = (cornerstoneWADOImageLoader as any).wadouri;

export interface RedactionRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const rectsByInstance = new Map<string, RedactionRect[]>();

const listeners = new Set<() => void>();

/**
 * Observe commits/clears. Used by the UI so "is this series redacted?" can be read
 * from this store — the one the export actually consults — rather than from a
 * parallel copy of the state that could drift out of sync with it.
 */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * The store key for an imageId: scheme + the instance-level url, with any frame
 * suffix removed.
 *
 * Both sides of the map — the commit (which keys off `instance.imageId`) and the
 * lookup in `applyTo` (which keys off a decoded frame's imageId) — must go through
 * this one function. They previously used different rules: the commit used the
 * imageId verbatim while the lookup split on the first "?". Those agree for
 * `dicomfile:5?frame=2`, but not for a `wadouri:` instance whose URL carries a
 * query string (`wadouri:https://host/x.dcm?token=a`), where the lookup truncated
 * to the bare URL, never matched, and silently exported unredacted pixels. Two
 * URLs sharing a path prefix also collided onto one key, so clearing one series'
 * redactions cleared another's.
 *
 * Delegating to WADO's own `parseImageId` keeps the split identical to the one the
 * loaders use to find the source file (it cuts at `frame=`, not at `?`).
 */
export function keyFor(imageId: string): string {
  const colonIndex = imageId.indexOf(":");
  const scheme = colonIndex === -1 ? "" : imageId.slice(0, colonIndex);
  const { url } = parseImageId(imageId);
  return `${scheme}:${url}`;
}

export function setForInstances(
  instanceImageIds: string[],
  rects: RedactionRect[]
): void {
  for (const imageId of instanceImageIds) {
    const key = keyFor(imageId);
    if (rects.length === 0) rectsByInstance.delete(key);
    // Copy so later edits to the caller's array can't retroactively change what
    // was committed.
    else rectsByInstance.set(key, rects.map((r) => ({ ...r })));
  }
  notify();
}

export function clearForInstances(instanceImageIds: string[]): void {
  for (const imageId of instanceImageIds) rectsByInstance.delete(keyFor(imageId));
  notify();
}

/** Committed rectangles for an instance or one of its frames, if any. */
export function get(imageId: string): RedactionRect[] | undefined {
  return rectsByInstance.get(keyFor(imageId));
}

/** True when this instance (or the instance owning this frame) is redacted. */
export function has(imageId: string): boolean {
  return (get(imageId)?.length ?? 0) > 0;
}

export function hasAny(): boolean {
  return rectsByInstance.size > 0;
}

/** Number of instances carrying rectangles. For the pre-export summary. */
export function instanceCount(): number {
  return rectsByInstance.size;
}

/**
 * Drop every rectangle. Must be called by any full session reset that also calls
 * `fileRegistry.releaseAll()` — that resets imageId minting to 0, so leftover
 * rectangles would re-attach themselves to whatever files are loaded next.
 */
export function clearAll(): void {
  rectsByInstance.clear();
  notify();
}

/**
 * Clamp a rectangle to a frame's bounds and convert it to sample offsets.
 *
 * Shared by the apply and verify paths so the two can't drift: the verifier checks
 * exactly the samples the applier claims to have written.
 */
export function clampRect(
  rect: RedactionRect,
  rows: number,
  columns: number,
  samplesPerPixel: number
): { startOffsetOfRow: (row: number) => number; rowSpan: number; top: number; height: number } {
  const left = Math.min(Math.max(rect.left, 0), columns);
  const width = Math.min(Math.max(rect.width, 0), columns - left);
  const top = Math.min(Math.max(rect.top, 0), rows);
  const height = Math.min(Math.max(rect.height, 0), rows - top);
  const stride = columns * samplesPerPixel;

  return {
    startOffsetOfRow: (row: number) => (top + row) * stride + left * samplesPerPixel,
    rowSpan: width * samplesPerPixel,
    top,
    height,
  };
}

/**
 * Samples per pixel implied by a decoded (or about-to-be-written) buffer: covers
 * grayscale (1), RGB (3) and the RGBA the color decode path produces (4) without
 * needing to consult the header.
 */
export function samplesPerPixelOf(
  sampleCount: number,
  rows: number,
  columns: number
): number {
  const samplesPerPixel = sampleCount / (rows * columns);
  if (!Number.isInteger(samplesPerPixel)) {
    throw new Error("Image size unknown, cannot redact");
  }
  return samplesPerPixel;
}

/** Zero the redacted rectangles in a decoded frame, in place. */
export function applyRedactionToPixelData(
  pixelData: TypedArray,
  rows: number,
  columns: number,
  redactions: RedactionRect[],
  smallestPixelValue: number
): void {
  const samplesPerPixel = samplesPerPixelOf(pixelData.length, rows, columns);
  const valueToZero = smallestPixelValue || 0;

  redactions.forEach((redaction) => {
    const { startOffsetOfRow, rowSpan, height } = clampRect(
      redaction,
      rows,
      columns,
      samplesPerPixel
    );
    for (let i = 0; i < height; i++) {
      const startIndex = startOffsetOfRow(i);
      // Zero the row span in place. TypedArray.fill avoids allocating a
      // per-redaction scratch Array of boxed numbers.
      pixelData.fill(valueToZero, startIndex, startIndex + rowSpan);
    }
  });
}

/**
 * The value `applyTo` fills a redacted region with, for a given decoded frame.
 * The verifier needs the identical value, so both read it from here.
 */
export function fillValueFor(image: any): number {
  return (image?.imageFrame?.smallestPixelValue as number) || 0;
}

/**
 * Apply this instance's committed redactions to a freshly decoded frame.
 * Called from the image loaders — every decode path must go through here, or
 * evicted frames come back unredacted.
 */
export function applyTo(image: any): void {
  if (rectsByInstance.size === 0) return;
  const rects = get(image?.imageId ?? "");
  if (!rects?.length) return;

  const frame = image.imageFrame;
  if (!frame?.pixelData) return;

  applyRedactionToPixelData(
    frame.pixelData as TypedArray,
    frame.rows,
    frame.columns,
    rects,
    fillValueFor(image)
  );
}
